import React, { useEffect, useState } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { HStack, Stack, Wrap } from '@chakra-ui/react';
import { each, isEmpty } from 'lodash';
import map from 'lodash/map';
import { useDispatch, useSelector } from 'react-redux';

import { ScanVaultCentralAccount } from 'api/scan-vault/client';
import { DetailsIdentifier, StackedHeader, Tag } from 'components/DataDisplay';
import { CancelButton, PrimaryButton, Select } from 'components/DataEntry';
import { AwsIcon, RegionIcon } from 'components/Icons';
import { Modal } from 'components/Overlay';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';
import { CentralScanningCommonFields } from 'containers/Setup/Outpost/Components/CentralScanningCommonFields';
import { OutpostInfoBox } from 'containers/Setup/Outpost/Components/utils';
import { selectOutpost } from 'containers/Setup/Outpost/selectors';
import { actions } from 'containers/Setup/Outpost/slice';
import { selectSharedState } from 'containers/SharedState/selectors';
import { actions as sharedStateActions } from 'containers/SharedState/slice';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  row: ScanVaultCentralAccount;
}

export const UpdateRegionsModal = (props: Props) => {
  const { isOpen, onClose, row } = props;
  const { cloudAccountIDMapping } = useCloudAccountId();
  const dispatch = useDispatch();
  const [regionOptions, setRegionOptions] = useState<Record<string, any>[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<Record<string, any>[]>(
    [],
  );
  const { regionsByProvider } = useSelector(selectSharedState);
  const { outpostActions } = useSelector(selectOutpost);

  useEffect(() => {
    setSelectedRegions(
      map(row.effective_enabled_regions, o => ({ label: o, value: o })),
    );
  }, [row]);

  useEffect(() => {
    dispatch(
      sharedStateActions.getRegionsByProvider({
        q: { provider: 'AWS' },
        onSuccess: res => {
          let options: Record<string, any>[] = [];
          each(res, o => {
            if (!row.effective_enabled_regions?.includes(o.code)) {
              options.push({ label: o.code, value: o.code });
            }
          });
          setRegionOptions(options);
        },
      }),
    );
  }, [dispatch, row]);

  const handleSubmit = () => {
    dispatch(
      actions.updateEnabledRegionsForScanning({
        q: {
          orgEnabledRegionsRequest: {
            regions: selectedRegions.map(o => o.value),
            provider: CloudProviders.Aws,
            account_id: cloudAccountIDMapping?.[row.account_id]?.uuid,
          },
        },
        onSuccess: () => {
          onClose();
          dispatch(actions.getAriksaOutposts({ q: {} }));
          dispatch(
            actions.getEnabledRegionsForScanning({
              q: { provider: CloudProviders.Aws },
            }),
          );
        },
      }),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      header={
        <StackedHeader
          upper="Update Regions"
          lower="Add or delete regions to scan for vulnerabilities"
          icon={<RegionIcon />}
        />
      }
      body={
        <Stack spacing={4}>
          <CentralScanningCommonFields row={row} />
          <OutpostInfoBox w="full" px={1} pb={2}>
            <DetailsIdentifier
              label="Regions enabled"
              value={
                <Select
                  placeholder="Select regions to enable"
                  options={regionOptions}
                  value={selectedRegions}
                  onChange={v => {
                    setSelectedRegions(v);
                  }}
                  isLoading={regionsByProvider.isLoading}
                  isDisabled={regionsByProvider.isLoading}
                  isMulti
                  hideSelectedOptions
                  menuPortalTarget={document.body}
                  showBorder={false}
                  showTotalSelected
                  showIconInValueContainer={false}
                />
              }
              isInline={false}
              styles={{ label: { px: 2, fontSize: '15px' } }}
              spacing={0}
            />
          </OutpostInfoBox>
          <Wrap>
            {map(selectedRegions, o => (
              <Tag
                label={o.label}
                leftIcon={<AwsIcon />}
                closeButton
                value={o.value}
                onClose={value => {
                  setSelectedRegions(
                    selectedRegions.filter(r => r.value !== value),
                  );
                }}
              />
            ))}
          </Wrap>
        </Stack>
      }
      footer={
        <HStack justify="space-between" w="full">
          <CancelButton
            onClick={() => {
              onClose();
            }}
          >
            CANCEL
          </CancelButton>
          <PrimaryButton
            px={6}
            onClick={handleSubmit}
            isDisabled={
              isEmpty(selectedRegions) ||
              regionsByProvider.isLoading ||
              outpostActions.isLoading
            }
            isLoading={regionsByProvider.isLoading || outpostActions.isLoading}
          >
            OK
          </PrimaryButton>
        </HStack>
      }
    />
  );
};
